import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { BaseRoute } from 'core/models/base-routes';
import { AnalyticsStructureStore } from 'core/stores/analytics-structure.store';
import { CircleButtonTab } from './circle-button-tab/circle-button-tab';
import { CircleButtonSize } from '../buttons/button-types';
import styles from './tabs.module.scss';

export enum TabStyle {
  Standard,
  CircleButtonTab,
}

export const Tabs = observer(({
  routes,
  tabStyle = TabStyle.Standard,
  tabSize = CircleButtonSize.ExtraLarge,
  compactTabs = false,
  showSingle = false,
  analyticsComponentName,
  analyticsAdditionalProps
}: {
  routes?: BaseRoute[];
  tabStyle?: TabStyle;
  tabSize?: CircleButtonSize;
  compactTabs?: boolean;
  showSingle?: boolean;
  analyticsComponentName?: string;
  analyticsAdditionalProps?: any;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const analyticsTags = (routeName: string) => {
    if (analyticsComponentName) {
      let overWriteKeys = {
        'ctaText': routeName
      }
      if (analyticsAdditionalProps) {
        overWriteKeys = { ...overWriteKeys, ...analyticsAdditionalProps };
      };
      const componentAnalytics = AnalyticsStructureStore.getComponentAnalyticsAsJson(
        analyticsComponentName,
        overWriteKeys
      );
      return {
        'data-component-name': analyticsComponentName,
        'data-component-info': JSON.stringify(componentAnalytics)
      };
    }
    return '';
  };

  const onClick = (route: BaseRoute) => {
    const curr = routes?.find((r) => r.isActive === true);
    if (curr) {
      curr.isActive = false;
    }
    route.isActive = !route.isActive;
    navigate(
      route?.path ? route?.path?.replace('*', '') : route?.children?.length ? route?.children[0].path || '/' : '/',
      {
        replace: true,
      }
    );
  };

  const positionCircleButtons =
    routes &&
    tabStyle === TabStyle.CircleButtonTab &&
    (routes?.length <= 3 ? 'justify-content-around' : 'justify-content-center flex-wrap');

  // TODO: Make Generic and move this to menu-tabbed-component
  return routes && (routes?.length > 1 || showSingle) ? (
    <div className={`${styles.tab}`}>
      <ul className={`${styles.tabList} ${positionCircleButtons}`}>
        {routes?.map(
          (route, i) =>
          ({
            [TabStyle.CircleButtonTab]: (
              <CircleButtonTab
                key={i}
                route={route}
                size={tabSize}
                onClick={onClick}
                styles={styles}
              ></CircleButtonTab>
            ),
            [TabStyle.Standard]: (
              <li
                className={compactTabs ? styles.compactListItem : styles.listItem}
                key={i}
                onClick={() => {
                  onClick(route);
                }}
                {...analyticsTags(t(`${route?.name}`))}
              >
                <a
                  className={`text-regular ${styles.listItemLink} ${route.isActive ||
                    (i === 0 && routes.findIndex((currentRoute) => currentRoute.isActive !== undefined) === -1)
                    ? styles.active
                    : ''
                    }`}
                  href="/#"
                  onClick={(e) => e.preventDefault()}
                >
                  {t(`${route?.name}`)}
                </a>
                {route.isActive && route.children
                  ? route.children.map((subRoute, subIndex) => (
                    <div
                      className={styles.subListItem}
                      key={subIndex}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(subRoute?.path || '/', {
                          replace: true,
                        });
                      }}
                      {...analyticsTags(t(`${route?.name} - ${subRoute?.name}`))}
                    >
                      <a className={styles.subListItemLink} href="/#" onClick={(e) => e.preventDefault()}>
                        {t(`${subRoute?.name}`)}
                      </a>
                    </div>
                  ))
                  : null}
              </li>
            ),
          }[tabStyle])
        )}
      </ul>
    </div>
  ) : null;
});